/**
 * NexusCodeApp — Root da aplicação NEXUS CODE (nexuscode.es).
 *
 * Router interno (sem usar React Router do Kaiso — completamente isolado):
 *   "/" (auth)     → NexusCodeAuth   (login/register)
 *   "/home"        → NexusCodeHome   (dashboard: "O que queres construir?")
 *   "/chat"        → NexusCodeChat   (workspace com stream + aprovações)
 *
 * Não usa o AuthContext do Kaiso.
 * Lê/escreve o token em localStorage com chave "nc_token".
 */
import React, { useState, useEffect } from "react";
import { ncToken, ncAuth } from "./nexuscodeApi";
import NexusCodeLanding from "./NexusCodeLanding";

import NexusCodeAuth from "./NexusCodeAuth";
import NexusCodeHome from "./NexusCodeHome";
import NexusCodeChat from "./NexusCodeChat";

export default function NexusCodeApp() {
  const [user,     setUser]     = useState(null);    // null = não autenticado
  const [loading,  setLoading]  = useState(true);    // a verificar token
  const [view,     setView]     = useState("home");  // "landing" | "auth" | "home" | "chat"
  const [chatCtx,  setChatCtx]  = useState(null);    // { convId, prompt, title }

  // ── Verificar token existente (incluindo callback OAuth) ────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthToken = params.get("nc_token");
    const oauthError = params.get("oauth_error");

    // Limpar URL sem recarregar a pagina
    if (oauthToken || oauthError) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (oauthError) {
      setLoading(false);
      setView("auth");
      return;
    }

    if (oauthToken) {
      ncToken.set(oauthToken);
    }

    if (!ncToken.exists()) { setLoading(false); setView("landing"); return; }
    ncAuth.me().then(({ data, error }) => {
      if (data) { setUser(data); setView("home"); }
      else      { ncToken.remove(); setView("landing"); }  // token inválido — limpa
      setLoading(false);
    });
  }, []);

  // ── Handlers ────────────────────────────────────────────────────
  function handleAuth(userData) {
    setUser(userData);
    setView("home");
  }

  function handleLogout() {
    ncToken.remove();
    setUser(null);
    setView("landing");
    setChatCtx(null);
  }

  function handleNewChat(prompt) {
    setChatCtx({ convId: null, prompt });
    setView("chat");
  }

  function handleSelectChat(convId, title) {
    setChatCtx({ convId, title, prompt: null });
    setView("chat");
  }

  function handleBackToHome() {
    setView("home");
    setChatCtx(null);
  }

  // ── Loading ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: "#030407", display: "flex",
        alignItems: "center", justifyContent: "center",
        color: "#64748b", fontSize: 13,
        fontFamily: "'Manrope', system-ui, sans-serif",
      }}>
        A carregar NEXUS CODE...
      </div>
    );
  }

  // ── Landing page (unauthenticated) ──────────────────────────────
  if (view === "landing") {
    return <NexusCodeLanding onGetStarted={() => setView("auth")} />;
  }

  // ── Auth (login/register) ────────────────────────────────────────
  if (!user || view === "auth") {
    return <NexusCodeAuth onAuth={handleAuth} onBack={() => setView("landing")} />;
  }

  // ── Chat workspace ───────────────────────────────────────────────
  if (view === "chat" && chatCtx) {
    return (
      <NexusCodeChat
        convId={chatCtx.convId}
        initPrompt={chatCtx.prompt}
        onBack={handleBackToHome}
        user={user}
      />
    );
  }

  // ── Home dashboard ───────────────────────────────────────────────
  return (
    <NexusCodeHome
      user={user}
      onNewChat={handleNewChat}
      onSelectChat={handleSelectChat}
      onLogout={handleLogout}
    />
  );
}
